
import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Header } from './components/Header';
import { Sidebar } from './components/Sidebar';
import { Dashboard } from './components/Dashboard';
import { AllTicketsPage } from './components/AllTicketsPage';
import { TicketBoardPage } from './components/TicketBoardPage'; // New
import { Footer } from './components/Footer';
import { NewTicketModal } from './components/NewTicketModal';
import { UpdateStatusModal } from './components/UpdateStatusModal';
import { CustomerSearchPage } from './components/CustomerSearchPage';
import { CustomerProfilePage } from './components/CustomerProfilePage';
import { SquarePaymentModal } from './components/SquarePaymentModal';
import { InventoryPage } from './components/InventoryPage';
import { SelfCheckInPage } from './components/SelfCheckInPage';
import { LabelsPage } from './components/LabelsPage';
import { PrintModal } from './components/PrintModal';
import { AdminUsersPage } from './components/AdminUsersPage';
import { AdminSettingsPage } from './components/AdminSettingsPage';
import { AdminUICustomizationPage } from './components/AdminUICustomizationPage';
import { AdminActivityLogPage } from './components/AdminActivityLogPage';
// import { AdminChecklistsPage } from './components/AdminChecklistsPage';
import { AdminBuybackSettingsPage } from './components/AdminBuybackSettingsPage';
import { SubmitSuggestionModal } from './components/SubmitSuggestionModal';
import { KnowledgeBasePage } from './components/KnowledgeBasePage';
import { AITroubleshooterPage } from './components/AITroubleshooterPage';
import { AIDiagnosticAssistantPage } from './components/AIDiagnosticAssistantPage';
import { CustomerPortalPage } from './components/CustomerPortalPage';
import { BuybackWidgetPage } from './components/BuybackWidgetPage';
import { ReportsPage } from './components/ReportsPage';
import { AppointmentsPage } from './components/AppointmentsPage';
import { AppointmentModal } from './components/AppointmentModal';
import { PreCompletionCheckModal } from './components/PreCompletionCheckModal';
import { MOCK_TICKETS, MOCK_CUSTOMERS, MOCK_EMPLOYEES, MOCK_INVENTORY_ITEMS, DEFAULT_APP_SETTINGS, MOCK_BUYBACK_REQUESTS, DEFAULT_REPAIR_STAGES_TEMPLATE, STATUS_FILTERS, DASHBOARD_CHART_COLORS, MOCK_REPAIR_CHECKLISTS, MOCK_APPOINTMENTS, INITIAL_BUYBACK_APPLE_MODELS_BY_TYPE, INITIAL_BUYBACK_PRICING_FACTORS, MOCK_TRADED_IN_DEVICES } from './constants';
import { Ticket, TicketStatus, FilterType, TicketPriority, YesNoUnknown, Customer, Employee, PaymentStatus, InventoryItem, AppSettings, BuybackRequest, BuybackDeviceType, BuybackCondition, InternalTicketNote, RepairStage, RepairStageStatus, CommunicationLogEntry, CommunicationType, TicketTrendData, SpecialTicketFilterCriteria, RepairBreakdownSegment, ActivityLogEntry, EmployeeRole, ActivityLogTargetType, RepairChecklist, Appointment, AppointmentModalData, DeviceModelInfo, BuybackPricingFactors, PartUsageEntry, PreCompletionCheckData, TradedInDevice } from './types';
import { calculateSlaForTicket } from './utils/slaUtils';
import { Button } from './components/Button';

export interface NewTicketFormData {
  customer: string;
  customerPhoneNumber?: string;
  customerEmail?: string;
  deviceModel?: string;
  deviceSerialNumber?: string;
  subject: string;
  description: string;
  priority: TicketPriority;
  devicePasscode?: string;
  dataBackupStatus?: YesNoUnknown;
  findMyDeviceDisabled?: YesNoUnknown;
  referralSource?: string;
  amountDue?: number;
  estimateDetails?: string;
  category?: string;
  aiRawSuggestion?: {
    category: string;
    priorityString: string;
  };
}

export interface TicketToUpdate {
  id: string;
  currentStatus: TicketStatus;
}

export interface TicketForPayment {
  id: string;
  amountDue: number;
  customerName: string;
}

export interface QuickAppraisalInitialData {
  deviceType?: BuybackDeviceType;
  modelId?: string;
  condition?: BuybackCondition;
}

type ActiveView = 'dashboard' | 'allTicketsView' | 'customerSearch' | 'customerProfile' | 'inventory' | 'selfCheckIn' | 'labels' | 'adminUsers' | 'adminSettings' | 'adminUICustomization' | 'adminActivityLog' /*| 'adminChecklists'*/ | 'adminBuybackSettings' | 'knowledgeBase' | 'aiTroubleshooter' | 'aiDiagnosticAssistant' | 'customerPortal' | 'buybackWidget' | 'ticketBoard' | 'reports' | 'appointments' | 'permissionDenied';


const convertPriorityStringToEnum = (priorityString?: string): TicketPriority | undefined => {
  if (!priorityString) return undefined;
  const upperPriorityString = priorityString.toUpperCase();
  if (upperPriorityString === 'HIGH') return TicketPriority.HIGH;
  if (upperPriorityString === 'MEDIUM') return TicketPriority.MEDIUM;
  if (upperPriorityString === 'LOW') return TicketPriority.LOW;
  if (upperPriorityString.startsWith('HIGH')) return TicketPriority.HIGH;
  if (upperPriorityString.startsWith('MEDIUM')) return TicketPriority.MEDIUM;
  if (upperPriorityString.startsWith('LOW')) return TicketPriority.LOW;
  return undefined;
};


const App = () => {
  const [tickets, setTickets] = useState<Ticket[]>(MOCK_TICKETS);
  const [customers, setCustomers] = useState<Customer[]>(MOCK_CUSTOMERS);
  const [employees, setEmployees] = useState<Employee[]>(MOCK_EMPLOYEES);
  const [inventoryItems, setInventoryItems] = useState<InventoryItem[]>(MOCK_INVENTORY_ITEMS);
  const [appSettings, setAppSettings] = useState<AppSettings>(DEFAULT_APP_SETTINGS);
  const [buybackRequests, setBuybackRequests] = useState<BuybackRequest[]>(MOCK_BUYBACK_REQUESTS);
  const [activityLog, setActivityLog] = useState<ActivityLogEntry[]>([]);
  const [repairChecklists, setRepairChecklists] = useState<RepairChecklist[]>(MOCK_REPAIR_CHECKLISTS);
  const [appointments, setAppointments] = useState<Appointment[]>(MOCK_APPOINTMENTS);
  const [tradedInDevices, setTradedInDevices] = useState<TradedInDevice[]>(MOCK_TRADED_IN_DEVICES);


  const [buybackDeviceModels, setBuybackDeviceModels] = useState<Record<BuybackDeviceType, DeviceModelInfo[]>>(
    JSON.parse(JSON.stringify(INITIAL_BUYBACK_APPLE_MODELS_BY_TYPE))
  );
  const [buybackPricingFactors, setBuybackPricingFactors] = useState<BuybackPricingFactors>(
    JSON.parse(JSON.stringify(INITIAL_BUYBACK_PRICING_FACTORS))
  );


  const [activeFilter, setActiveFilter] = useState<FilterType>(TicketStatus.NEW);
  const [ticketSearchTerm, setTicketSearchTerm] = useState('');
  const [isNewTicketModalOpen, setIsNewTicketModalOpen] = useState(false);
  const [isUpdateStatusModalOpen, setIsUpdateStatusModalOpen] = useState(false);
  const [ticketToUpdateStatus, setTicketToUpdateStatus] = useState<TicketToUpdate | null>(null);

  const [activeView, setActiveView] = useState<ActiveView>('dashboard');
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(null);
  const [ticketIdForCustomerPortal, setTicketIdForCustomerPortal] = useState<string | null>(null);

  const [isSquarePaymentModalOpen, setIsSquarePaymentModalOpen] = useState(false);
  const [ticketForPayment, setTicketForPayment] = useState<TicketForPayment | null>(null);

  const [inventoryImportLoading, setInventoryImportLoading] = useState(false);
  const [lastInventoryImportTime, setLastInventoryImportTime] = useState<string | undefined>(
    MOCK_INVENTORY_ITEMS.filter(item => item.source === 'square').reduce((latest, item) => {
      if (!item.lastSyncedAt) return latest;
      if (!latest || new Date(item.lastSyncedAt) > new Date(latest)) {
        return item.lastSyncedAt;
      }
      return latest;
    }, undefined as string | undefined)
  );

  const [isPrintModalOpen, setIsPrintModalOpen] = useState(false);
  const [printModalTitle, setPrintModalTitle] = useState('');
  const [printableContent, setPrintableContent] = useState<React.ReactNode>(null);
  const [printContentClassName, setPrintContentClassName] = useState<string | undefined>(undefined);

  const [isSubmitSuggestionModalOpen, setIsSubmitSuggestionModalOpen] = useState(false);
  const [quickAppraisalInitialData, setQuickAppraisalInitialData] = useState<QuickAppraisalInitialData | undefined>(undefined);

  const [ticketTrendData, setTicketTrendData] = useState<TicketTrendData | null>(null);
  const [specialTicketFilterCriteria, setSpecialTicketFilterCriteria] = useState<SpecialTicketFilterCriteria>(null);

  const [currentUser, setCurrentUser] = useState<Employee | null>(() => {
    const admin = MOCK_EMPLOYEES.find(e => e.role === 'Administrator' && e.status === 'Active');
    if (admin) return admin;
    const activeUser = MOCK_EMPLOYEES.find(e => e.status === 'Active');
    return activeUser || MOCK_EMPLOYEES[0] || null;
  });

  const [isAppointmentModalOpen, setIsAppointmentModalOpen] = useState(false);
  const [appointmentModalData, setAppointmentModalData] = useState<AppointmentModalData | null>(null);

  const [isPreCompletionModalOpen, setIsPreCompletionModalOpen] = useState(false);
  const [dataForPreCompletionCheck, setDataForPreCompletionCheck] = useState<PreCompletionCheckData | null>(null);


  const handleSetCurrentUser = useCallback((employeeId: string) => {
    const user = MOCK_EMPLOYEES.find(e => e.id === employeeId);
    setCurrentUser(user || null);
    setActiveView('dashboard');
    clearActiveSubViews();
  }, []);

  const currentUserNameForLog = useMemo(() => currentUser?.name || "System/Unknown User", [currentUser]);


  const logActivity = useCallback((user: string, action: string, details?: string, targetType?: ActivityLogTargetType, targetId?: string) => {
    const newLogEntry: ActivityLogEntry = {
      id: `LOG-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`,
      timestamp: new Date().toISOString(),
      user,
      action,
      details,
      targetType,
      targetId,
    };
    setActivityLog(prevLog => [newLogEntry, ...prevLog].slice(0, 200));
  }, []);

  useEffect(() => {
    logActivity(currentUserNameForLog, "Application Started / User Switched", `Current user: ${currentUserNameForLog}`, ActivityLogTargetType.SYSTEM);
  }, [currentUserNameForLog, logActivity]);


  useEffect(() => {
    document.documentElement.style.setProperty('--color-primary', appSettings.primaryColor);
    document.documentElement.style.setProperty('--color-accent', appSettings.accentColor);
  }, [appSettings.primaryColor, appSettings.accentColor]);

  useEffect(() => {
    const today = new Date();
    const endOfCurrentPeriod = new Date(today);
    const startOfCurrentPeriod = new Date(today);
    startOfCurrentPeriod.setDate(today.getDate() - 7);
    startOfCurrentPeriod.setHours(0, 0, 0, 0);

    const endOfPreviousPeriod = new Date(startOfCurrentPeriod);
    endOfPreviousPeriod.setDate(startOfCurrentPeriod.getDate() -1);
    endOfPreviousPeriod.setHours(23,59,59,999);

    const startOfPreviousPeriod = new Date(endOfPreviousPeriod);
    startOfPreviousPeriod.setDate(endOfPreviousPeriod.getDate() - 7);
    startOfPreviousPeriod.setHours(0,0,0,0);

    const currentPeriodTickets = tickets.filter(t => {
        const createdAt = new Date(t.createdAt);
        return createdAt >= startOfCurrentPeriod && createdAt <= endOfCurrentPeriod;
    });
    const previousPeriodTickets = tickets.filter(t => {
        const createdAt = new Date(t.createdAt);
        return createdAt >= startOfPreviousPeriod && createdAt <= endOfPreviousPeriod;
    });

    const currentPeriodCount = currentPeriodTickets.length;
    const previousPeriodCount = previousPeriodTickets.length;
    let percentageChange = 0;
    let trendDirection: TicketTrendData['trendDirection'] = 'steady';

    if (previousPeriodCount > 0) {
      percentageChange = ((currentPeriodCount - previousPeriodCount) / previousPeriodCount) * 100;
    } else if (currentPeriodCount > 0) {
      percentageChange = 100;
    }

    if (percentageChange > 5) trendDirection = 'up';
    else if (percentageChange < -5) trendDirection = 'down';

    setTicketTrendData({
      currentPeriodCount,
      previousPeriodCount,
      percentageChange: parseFloat(percentageChange.toFixed(1)),
      trendDirection,
    });
  }, [tickets]);

  const repairBreakdownData = useMemo((): RepairBreakdownSegment[] => {
    const categoryCounts: Record<string, number> = {};
    let totalTickets = 0;

    if (!Array.isArray(tickets)) {
        console.error("repairBreakdownData: tickets is not an array", tickets);
        return [];
    }

    tickets.forEach(ticket => {
      const category = ticket.category || 'Uncategorized';
      categoryCounts[category] = (categoryCounts[category] || 0) + 1;
      totalTickets++;
    });

    if (totalTickets === 0) return [];

    const chartColors = (Array.isArray(DASHBOARD_CHART_COLORS) && DASHBOARD_CHART_COLORS.length > 0)
                        ? DASHBOARD_CHART_COLORS
                        : ['bg-gray-500', 'bg-blue-500', 'bg-green-500', 'bg-yellow-500', 'bg-red-500'];

    return Object.entries(categoryCounts)
      .map(([type, count], index) => ({
        type,
        count,
        percentage: parseFloat(((count / totalTickets) * 100).toFixed(1)),
        color: chartColors[index % chartColors.length]
      }))
      .sort((a, b) => b.count - a.count);
  }, [tickets]);


  const clearActiveSubViews = () => {
    setSelectedCustomerId(null);
    setTicketIdForCustomerPortal(null);
    setQuickAppraisalInitialData(undefined);
    setAppointmentModalData(null);
    setDataForPreCompletionCheck(null);
  };

  const showAllTicketsView = useCallback((initialSearchTerm?: string, initialFilter?: FilterType) => {
    setActiveView('allTicketsView');
    if (initialFilter) setActiveFilter(initialFilter);
    if (initialSearchTerm) setTicketSearchTerm(initialSearchTerm);
    clearActiveSubViews();
  }, []);

  const showTicketBoardView = useCallback(() => {
    setActiveView('ticketBoard');
    clearActiveSubViews();
  }, []);

  const showDashboardView = useCallback(() => {
    setActiveView('dashboard');
    clearActiveSubViews();
  }, []);

  const showCustomerSearchView = useCallback(() => {
    setActiveView('customerSearch');
    clearActiveSubViews();
  }, []);

  const showInventoryView = useCallback(() => {
    setActiveView('inventory');
    clearActiveSubViews();
  }, []);

  const showSelfCheckInView = useCallback(() => {
    setActiveView('selfCheckIn');
    clearActiveSubViews();
  }, []);

  const showBuybackWidgetView = useCallback((initialData?: QuickAppraisalInitialData) => {
    if (initialData) {
      setQuickAppraisalInitialData(initialData);
    } else {
      setQuickAppraisalInitialData(undefined);
    }
    setActiveView('buybackWidget');
  }, []);

  const showLabelsView = useCallback(() => {
    setActiveView('labels');
    clearActiveSubViews();
  }, []);

  const openAppointmentModal = useCallback((data?: AppointmentModalData) => {
    setAppointmentModalData(data || {});
    setIsAppointmentModalOpen(true);
  }, []);

  const showAppointmentsView = useCallback((date?: string) => {
    setActiveView('appointments');
    clearActiveSubViews();
  }, []);


  const showAdminUsersView = useCallback(() => {
    if (currentUser?.role !== 'Administrator') {
        setActiveView('permissionDenied');
        return;
    }
    setActiveView('adminUsers');
    clearActiveSubViews();
  }, [currentUser]);

  const showAdminSettingsView = useCallback(() => {
    if (currentUser?.role !== 'Administrator') {
        setActiveView('permissionDenied');
        return;
    }
    setActiveView('adminSettings');
    clearActiveSubViews();
  }, [currentUser]);

  const showAdminUICustomizationView = useCallback(() => {
    if (currentUser?.role !== 'Administrator') {
        setActiveView('permissionDenied');
        return;
    }
    setActiveView('adminUICustomization');
    clearActiveSubViews();
  }, [currentUser]);

  const showAdminActivityLogView = useCallback(() => {
    if (currentUser?.role !== 'Administrator') {
        setActiveView('permissionDenied');
        return;
    }
    setActiveView('adminActivityLog');
    clearActiveSubViews();
  }, [currentUser]);

  const showAdminBuybackSettingsView = useCallback(() => {
    if (currentUser?.role !== 'Administrator') {
        setActiveView('permissionDenied');
        return;
    }
    setActiveView('adminBuybackSettings');
    clearActiveSubViews();
  }, [currentUser]);


  const showReportsView = useCallback(() => {
    setActiveView('reports');
    clearActiveSubViews();
  }, []);

  const showKnowledgeBaseView = useCallback(() => {
    setActiveView('knowledgeBase');
    clearActiveSubViews();
  }, []);

  const showAITroubleshooterView = useCallback(() => {
    setActiveView('aiTroubleshooter');
    clearActiveSubViews();
  }, []);

  const showAIDiagnosticAssistantView = useCallback(() => {
    setActiveView('aiDiagnosticAssistant');
    clearActiveSubViews();
  }, []);

  const showCustomerPortalView = useCallback((ticketId: string) => {
    setTicketIdForCustomerPortal(ticketId);
    setActiveView('customerPortal');
    setSelectedCustomerId(null);
    setQuickAppraisalInitialData(undefined);
  }, []);

  const showCustomerProfile = useCallback((customerId: string) => {
    setSelectedCustomerId(customerId);
    setActiveView('customerProfile');
    setTicketIdForCustomerPortal(null);
    setQuickAppraisalInitialData(undefined);
  }, []);

  const handleViewCustomerByName = useCallback((customerName: string) => {
    const customer = customers.find(c => c.name === customerName);
    if (customer) {
      showCustomerProfile(customer.id);
    } else {
      console.warn(`Customer "${customerName}" not found.`);
      showCustomerSearchView();
    }
  }, [customers, showCustomerProfile, showCustomerSearchView]);

  const handleOpenTicketFromDashboard = useCallback((ticketId: string) => {
    setSpecialTicketFilterCriteria(null);
    showAllTicketsView(ticketId, TicketStatus.NEW);
  }, [showAllTicketsView]);

  const handleShowUnpaidTicketsView = useCallback(() => {
    setSpecialTicketFilterCriteria('unpaid');
    setActiveFilter('ALL');
    setTicketSearchTerm('');
    setActiveView('allTicketsView');
    clearActiveSubViews();
  }, []);

  const openNewTicketModal = useCallback(() => {
    if (currentUser?.status === 'Inactive') {
        alert("Inactive users cannot create new tickets.");
        return;
    }
    setIsNewTicketModalOpen(true);
  }, [currentUser]);

  const closeNewTicketModal = useCallback(() => {
    setIsNewTicketModalOpen(false);
  }, []);

  const handleAddCommunicationLog = useCallback((
    ticketId: string,
    type: CommunicationType,
    content: string,
    recipient?: string,
    staffNameInput?: string
  ) => {
    setTickets(prevTickets =>
      prevTickets.map(ticket => {
        if (ticket.id === ticketId) {
          const newLogEntry: CommunicationLogEntry = {
            id: `CL-${ticket.id}-${Date.now()}`,
            type,
            timestamp: new Date().toISOString(),
            content,
            recipient: recipient || ticket.customerPhoneNumber || ticket.customerEmail,
            staffName: staffNameInput || currentUserNameForLog,
          };
          return {
            ...ticket,
            communicationLog: [newLogEntry, ...ticket.communicationLog],
          };
        }
        return ticket;
      })
    );
  }, [currentUserNameForLog]);

  const handleSaveTicket = useCallback((formData: NewTicketFormData): string => {
    let customerProfile = customers.find(c => c.name.toLowerCase() === formData.customer.toLowerCase());
    if (!customerProfile) {
      const newCustomerId = `CUST-${String(customers.length + 1 + Math.floor(Math.random() * 1000)).padStart(3, '0')}`;
      customerProfile = {
        id: newCustomerId,
        name: formData.customer,
        phoneNumber: formData.customerPhoneNumber,
        email: formData.customerEmail,
        createdAt: new Date().toISOString(),
        notes: [{ text: 'Profile auto-created with new ticket.', date: new Date().toISOString() }]
      };
      setCustomers(prev => [...prev, customerProfile!]);
      logActivity(currentUserNameForLog, `Created Customer: ${customerProfile.name}`, `Via new ticket`, ActivityLogTargetType.CUSTOMER, newCustomerId);
    } else {
      setCustomers(prev => prev.map(c => c.id === customerProfile!.id ? {
        ...c,
        phoneNumber: formData.customerPhoneNumber || c.phoneNumber,
        email: formData.customerEmail || c.email,
      } : c));
    }

    const initialRepairProgress = JSON.parse(JSON.stringify(DEFAULT_REPAIR_STAGES_TEMPLATE)) as RepairStage[];
    const now = new Date().toISOString();
    const receivedStage = initialRepairProgress.find(s => s.id === 'received');
    if (receivedStage) {
        receivedStage.status = RepairStageStatus.COMPLETED;
        receivedStage.completedAt = now;
    }
    const diagnosisStage = initialRepairProgress.find(s => s.id === 'diagnosis');
    if (diagnosisStage) {
        diagnosisStage.status = RepairStageStatus.IN_PROGRESS;
        diagnosisStage.startedAt = now;
    }
    if (!formData.amountDue || formData.amountDue === 0) {
        const estimateSentStage = initialRepairProgress.find(s => s.id === 'estimate_sent');
        if (estimateSentStage) estimateSentStage.status = RepairStageStatus.SKIPPED;
        const approvalWaitStage = initialRepairProgress.find(s => s.id === 'approval_wait');
        if (approvalWaitStage) approvalWaitStage.status = RepairStageStatus.SKIPPED;
    }

    const newTicketId = `TM-${String(tickets.length + 1 + Math.floor(Math.random() * 1000)).padStart(3, '0')}`;
    const newTicket: Ticket = {
      id: newTicketId,
      subject: formData.subject,
      description: formData.description,
      status: formData.amountDue && formData.amountDue > 0 ? TicketStatus.WAITING_FOR_CUSTOMER_APPROVAL : TicketStatus.NEW,
      priority: formData.priority,
      customer: customerProfile.name,
      customerPhoneNumber: formData.customerPhoneNumber,
      customerEmail: formData.customerEmail,
      deviceModel: formData.deviceModel,
      deviceSerialNumber: formData.deviceSerialNumber,
      devicePasscode: formData.devicePasscode,
      dataBackupStatus: formData.dataBackupStatus,
      findMyDeviceDisabled: formData.findMyDeviceDisabled,
      referralSource: formData.referralSource,
      assignedTo: undefined,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      amountDue: Number(formData.amountDue) || 0,
      paymentStatus: formData.amountDue && formData.amountDue > 0 ? PaymentStatus.UNPAID : PaymentStatus.PAID,
      estimateDetails: formData.estimateDetails || `Standard diagnostic and initial repair quote for ${formData.subject}.`,
      isEstimateApproved: formData.amountDue && formData.amountDue > 0 ? undefined : true,
      internalNotes: [],
      repairProgress: initialRepairProgress,
      communicationLog: [],
      category: formData.category,
      aiSuggestedCategory: formData.aiRawSuggestion?.category,
      aiSuggestedPriority: convertPriorityStringToEnum(formData.aiRawSuggestion?.priorityString),
      checklistProgress: {},
      partsUsed: [],
    };
    setTickets(prevTickets => [newTicket, ...prevTickets]);
    logActivity(currentUserNameForLog, `Created Ticket: ${newTicketId}`, `Subject: ${newTicket.subject}`, ActivityLogTargetType.TICKET, newTicketId);
    closeNewTicketModal();
    return newTicketId;
  }, [customers, tickets.length, closeNewTicketModal, logActivity, currentUserNameForLog]);

  const openUpdateStatusModal = useCallback((ticketId: string, currentStatus: TicketStatus) => {
    if (currentUser?.status === 'Inactive') {
        alert("Inactive users cannot update ticket status.");
        return;
    }
    setTicketToUpdateStatus({ id: ticketId, currentStatus });
    setIsUpdateStatusModalOpen(true);
  }, [currentUser]);

  const closeUpdateStatusModal = useCallback(() => {
    setTicketToUpdateStatus(null);
    setIsUpdateStatusModalOpen(false);
  }, []);

  const performTicketStatusUpdate = useCallback((ticketId: string, newStatus: TicketStatus, sendSms?: boolean, sendEmail?: boolean) => {
    const ticket = tickets.find(t => t.id === ticketId);
    if (!ticket) return;

    setTickets(prevTickets =>
      prevTickets.map(t => {
        if (t.id !== ticketId) return t;

        const updatedRepairProgress = [...t.repairProgress];
        const now = new Date().toISOString();

        const updateStage = (id: string, status: RepairStageStatus, setTime: boolean = true) => {
            const stage = updatedRepairProgress.find(s => s.id === id);
            if (stage) {
                stage.status = status;
                if (setTime) {
                    if (status === RepairStageStatus.COMPLETED || status === RepairStageStatus.SKIPPED) stage.completedAt = now;
                    else if (status === RepairStageStatus.IN_PROGRESS && !stage.startedAt) stage.startedAt = now;
                }
            }
        };

        if (newStatus === TicketStatus.IN_PROGRESS) {
            updateStage('diagnosis', RepairStageStatus.COMPLETED);
            if (t.isEstimateApproved || t.amountDue === 0) {
                 updateStage('estimate_sent', RepairStageStatus.COMPLETED);
                 updateStage('approval_wait', RepairStageStatus.COMPLETED);
            }
            updateStage('repairing', RepairStageStatus.IN_PROGRESS);
        } else if (newStatus === TicketStatus.WAITING_FOR_PARTS) {
             updateStage('diagnosis', RepairStageStatus.COMPLETED);
             if (t.isEstimateApproved || t.amountDue === 0) {
                 updateStage('estimate_sent', RepairStageStatus.COMPLETED);
                 updateStage('approval_wait', RepairStageStatus.COMPLETED);
             }
             updateStage('parts_order', RepairStageStatus.IN_PROGRESS);
        } else if (newStatus === TicketStatus.READY_FOR_PICKUP) {
            ['received', 'diagnosis', 'estimate_sent', 'approval_wait', 'parts_order', 'repairing', 'quality_check'].forEach(id => {
                const stage = updatedRepairProgress.find(s => s.id === id);
                if (stage && stage.status !== RepairStageStatus.SKIPPED) updateStage(id, RepairStageStatus.COMPLETED);
            });
            updateStage('pickup_ready', RepairStageStatus.IN_PROGRESS);
        } else if (newStatus === TicketStatus.COMPLETED) {
             DEFAULT_REPAIR_STAGES_TEMPLATE.forEach(defStage => {
                const stage = updatedRepairProgress.find(s => s.id === defStage.id);
                if(stage && stage.status !== RepairStageStatus.SKIPPED) updateStage(defStage.id, RepairStageStatus.COMPLETED)
             });
        }
        return { ...t, status: newStatus, updatedAt: new Date().toISOString(), repairProgress: updatedRepairProgress };
      })
    );
    logActivity(currentUserNameForLog, `Updated Status of ${ticketId} to ${newStatus}`, undefined, ActivityLogTargetType.TICKET, ticketId);

    if (newStatus === TicketStatus.READY_FOR_PICKUP && sendSms) {
      const smsContent = `Automated SMS: Ticket ${ticketId} for "${ticket.subject}" is now ${newStatus}.`;
      console.log(`Simulating SMS to customer for ticket ${ticketId}: ${smsContent}`);
      alert(`SMS Notification (Simulated): Ticket ${ticketId} is ${newStatus}!`);
      handleAddCommunicationLog(ticketId,CommunicationType.SYSTEM, smsContent, ticket.customerPhoneNumber, "System Automation");
    }
    if (newStatus === TicketStatus.COMPLETED && sendEmail) {
        const emailContent = `Service Summary Email queued for ticket ${ticketId} ("${ticket.subject}"). Status: ${newStatus}.`;
        console.log(`Simulating Service Summary Email for ticket ${ticketId}: ${emailContent}`);
        alert(`Service Summary Email (Simulated): Ticket ${ticketId} is ${newStatus}!`);
        handleAddCommunicationLog(ticketId, CommunicationType.SYSTEM, emailContent, ticket.customerEmail, "System Automation");
        logActivity(currentUserNameForLog, `Queued Service Summary Email for ${ticketId}`, undefined, ActivityLogTargetType.TICKET, ticketId);
    }

    closeUpdateStatusModal();
  }, [tickets, handleAddCommunicationLog, logActivity, currentUserNameForLog, closeUpdateStatusModal]);

  const handleRequestStatusUpdate = useCallback((ticketId: string, newStatus: TicketStatus, sendSms?: boolean) => {
    const ticket = tickets.find(t => t.id === ticketId);
    if (!ticket) return;

    if ((newStatus === TicketStatus.COMPLETED || newStatus === TicketStatus.READY_FOR_PICKUP) && currentUser?.role !== 'Administrator') {
        setDataForPreCompletionCheck({
            ticket,
            targetStatus: newStatus,
            targetSendSms: sendSms,
            targetSendCompletionEmail: newStatus === TicketStatus.COMPLETED, // Default for completed status
        });
        setIsPreCompletionModalOpen(true);
    } else {
        performTicketStatusUpdate(ticketId, newStatus, sendSms, newStatus === TicketStatus.COMPLETED);
    }
    closeUpdateStatusModal();
  }, [tickets, performTicketStatusUpdate, closeUpdateStatusModal, currentUser]);

  const handleProceedWithStatusUpdateFromPreCheck = useCallback((updatedData: PreCompletionCheckData) => {
    if (updatedData) {
        performTicketStatusUpdate(
            updatedData.ticket.id,
            updatedData.targetStatus,
            updatedData.targetSendSms,
            updatedData.targetSendCompletionEmail
        );
    }
    setIsPreCompletionModalOpen(false);
    setDataForPreCompletionCheck(null);
  }, [performTicketStatusUpdate]);

  const closePreCompletionModal = useCallback(() => {
    setIsPreCompletionModalOpen(false);
    setDataForPreCompletionCheck(null);
  }, []);
  
  const handleAddInternalTicketNote = useCallback((ticketId: string, noteText: string, employeeName?: string) => {
    if (currentUser?.status === 'Inactive') {
        alert("Inactive users cannot add internal ticket notes.");
        return;
    }
    const staffName = employeeName || currentUserNameForLog;
    setTickets(prevTickets =>
      prevTickets.map(ticket => {
        if (ticket.id === ticketId) {
          const newNote: InternalTicketNote = {
            id: `IN-${ticket.id}-${ticket.internalNotes.length + 1}-${Date.now()}`,
            text: noteText,
            createdAt: new Date().toISOString(),
            createdBy: staffName,
          };
          logActivity(staffName, `Added Internal Note to ${ticketId}`, noteText.substring(0,50)+'...', ActivityLogTargetType.TICKET, ticketId);
          return { ...ticket, internalNotes: [newNote, ...ticket.internalNotes] };
        }
        return ticket;
      })
    );
  }, [logActivity, currentUserNameForLog, currentUser]);


  const handleSavePreCheckNotesAndClose = useCallback((ticketId: string, notes: string) => {
      handleAddInternalTicketNote(ticketId, `Pre-Completion Check Note: ${notes}`, currentUserNameForLog);
      closePreCompletionModal();
  }, [handleAddInternalTicketNote, currentUserNameForLog, closePreCompletionModal]);


  const handleUpdateTicketStatusFromBoard = useCallback((ticketId: string, newStatus: TicketStatus) => {
    const ticket = tickets.find(t => t.id === ticketId);
    if (!ticket) return;
     if (currentUser?.status === 'Inactive') {
        alert("Inactive users cannot update ticket status from board.");
        return;
    }
    setTickets(prevTickets =>
      prevTickets.map(t => {
        if (t.id !== ticketId) return t;

        const updatedRepairProgress = [...t.repairProgress];
        const now = new Date().toISOString();
        const updateStage = (id: string, status: RepairStageStatus, setTime: boolean = true) => {
            const stage = updatedRepairProgress.find(s => s.id === id);
            if (stage) {
                stage.status = status;
                if (setTime) {
                    if (status === RepairStageStatus.COMPLETED || status === RepairStageStatus.SKIPPED) stage.completedAt = now;
                    else if (status === RepairStageStatus.IN_PROGRESS && !stage.startedAt) stage.startedAt = now;
                }
            }
        };

        if (newStatus === TicketStatus.IN_PROGRESS) {
            updateStage('diagnosis', RepairStageStatus.COMPLETED);
            if (t.isEstimateApproved || t.amountDue === 0) {
                 updateStage('estimate_sent', RepairStageStatus.COMPLETED);
                 updateStage('approval_wait', RepairStageStatus.COMPLETED);
            }
            updateStage('repairing', RepairStageStatus.IN_PROGRESS);
        } else if (newStatus === TicketStatus.WAITING_FOR_PARTS) {
             updateStage('diagnosis', RepairStageStatus.COMPLETED);
             if (t.isEstimateApproved || t.amountDue === 0) {
                 updateStage('estimate_sent', RepairStageStatus.COMPLETED);
                 updateStage('approval_wait', RepairStageStatus.COMPLETED);
             }
             updateStage('parts_order', RepairStageStatus.IN_PROGRESS);
        } else if (newStatus === TicketStatus.READY_FOR_PICKUP) {
            ['received', 'diagnosis', 'estimate_sent', 'approval_wait', 'parts_order', 'repairing', 'quality_check'].forEach(id => {
                const stage = updatedRepairProgress.find(s => s.id === id);
                if (stage && stage.status !== RepairStageStatus.SKIPPED) updateStage(id, RepairStageStatus.COMPLETED);
            });
            updateStage('pickup_ready', RepairStageStatus.IN_PROGRESS);
        } else if (newStatus === TicketStatus.COMPLETED) {
             DEFAULT_REPAIR_STAGES_TEMPLATE.forEach(defStage => {
                const stage = updatedRepairProgress.find(s => s.id === defStage.id);
                if(stage && stage.status !== RepairStageStatus.SKIPPED) updateStage(defStage.id, RepairStageStatus.COMPLETED)
             });
        }

        const newLogEntry: CommunicationLogEntry = {
            id: `CL-${t.id}-${Date.now()}`,
            type: CommunicationType.SYSTEM,
            timestamp: now,
            content: `Status changed to ${newStatus} via Ticket Board.`,
            staffName: currentUserNameForLog || "System (Board)",
        };

        return { ...t, status: newStatus, updatedAt: now, repairProgress: updatedRepairProgress, communicationLog: [newLogEntry, ...t.communicationLog] };
      })
    );
    logActivity(currentUserNameForLog, `Updated Status of ${ticketId} to ${newStatus} via Board`, undefined, ActivityLogTargetType.TICKET, ticketId);
  }, [tickets, logActivity, currentUserNameForLog, currentUser]);

  const handleUpdateRepairStage = useCallback((ticketId: string, stageId: string, newStageStatus: RepairStageStatus, stageNotes?: string) => {
    if (currentUser?.status === 'Inactive') {
        alert("Inactive users cannot update repair stages.");
        return;
    }
    setTickets(prevTickets => prevTickets.map(ticket => {
      if (ticket.id === ticketId) {
        const oldStage = ticket.repairProgress.find(s => s.id === stageId);
        const updatedProgress = ticket.repairProgress.map(stage => {
          if (stage.id === stageId) {
            const updatedStage = { ...stage, status: newStageStatus };
            if (stageNotes) updatedStage.notes = stageNotes;
            const now = new Date().toISOString();
            if (newStageStatus === RepairStageStatus.IN_PROGRESS && !stage.startedAt) {
              updatedStage.startedAt = now;
            } else if (newStageStatus === RepairStageStatus.COMPLETED || newStageStatus === RepairStageStatus.SKIPPED) {
              updatedStage.completedAt = now;
              if (!updatedStage.startedAt && newStageStatus === RepairStageStatus.COMPLETED) updatedStage.startedAt = now;
            }
            return updatedStage;
          }
          return stage;
        });

        if (newStageStatus === RepairStageStatus.COMPLETED) {
            const currentStageIndex = updatedProgress.findIndex(s => s.id === stageId);
            if (currentStageIndex !== -1 && currentStageIndex < updatedProgress.length - 1) {
                const nextStage = updatedProgress[currentStageIndex + 1];
                if (nextStage && nextStage.status === RepairStageStatus.PENDING) {
                    nextStage.status = RepairStageStatus.IN_PROGRESS;
                    nextStage.startedAt = new Date().toISOString();
                }
            }
        }
        logActivity(currentUserNameForLog, `Updated Repair Stage for ${ticketId}`, `Stage: ${oldStage?.name} to ${newStageStatus}. Notes: ${stageNotes ? stageNotes.substring(0,30)+'...' : 'N/A'}`, ActivityLogTargetType.TICKET, ticketId);
        return { ...ticket, repairProgress: updatedProgress, updatedAt: new Date().toISOString() };
      }
      return ticket;
    }));
  }, [logActivity, currentUserNameForLog, currentUser]);

  const handleAddCustomerNote = useCallback((customerId: string, noteText: string) => {
     if (currentUser?.status === 'Inactive') {
        alert("Inactive users cannot add customer notes.");
        return;
    }
    let customerName = "Unknown Customer";
    setCustomers(prevCustomers =>
      prevCustomers.map(customer => {
        if (customer.id === customerId) {
          customerName = customer.name;
          return { ...customer, notes: [...customer.notes, { text: noteText, date: new Date().toISOString() }] };
        }
        return customer;
      })
    );
    logActivity(currentUserNameForLog, `Added Note to Customer ${customerName}`, noteText.substring(0,50)+'...', ActivityLogTargetType.CUSTOMER, customerId);
  }, [logActivity, currentUserNameForLog, currentUser]);


  const openSquarePaymentModal = useCallback((ticketId: string, amountDue: number, customerName: string) => {
    const userRole = currentUser?.role as EmployeeRole;
    if (currentUser?.status === 'Inactive' || userRole === 'Technician') {
        alert("You do not have permission to process payments.");
        return;
    }
    setTicketForPayment({ id: ticketId, amountDue, customerName });
    setIsSquarePaymentModalOpen(true);
  }, [currentUser]);

  const closeSquarePaymentModal = useCallback(() => {
    setTicketForPayment(null);
    setIsSquarePaymentModalOpen(false);
  }, []);

  const handlePaymentSuccess = useCallback((ticketId: string, paymentToken: string, cardBrand: string, last4: string) => {
    console.log(`Payment token received for ticket ${ticketId}: ${paymentToken}. Card: ${cardBrand} ending in ${last4}`);
    setTickets(prevTickets =>
      prevTickets.map(ticket => {
        if (ticket.id === ticketId) {
          const updatedRepairProgress = ticket.repairProgress.map(stage =>
            stage.id === 'completed_paid' ? { ...stage, status: RepairStageStatus.COMPLETED, completedAt: new Date().toISOString() } : stage
          );
          return {
            ...ticket,
            paymentStatus: PaymentStatus.PAID,
            status: TicketStatus.COMPLETED,
            repairProgress: updatedRepairProgress,
            updatedAt: new Date().toISOString()
          };
        }
        return ticket;
      })
    );
    alert(`Payment for ticket ${ticketId} processed successfully (Simulated).\nCard: ${cardBrand} ****${last4}`);

    handleAddCommunicationLog(
      ticketId,
      CommunicationType.SYSTEM,
      `Payment of $${ticketForPayment?.amountDue.toFixed(2)} processed successfully via Square. Card: ${cardBrand} ****${last4}.`,
      undefined,
      "Square Integration"
    );
    logActivity(currentUserNameForLog, `Payment Success for Ticket ${ticketId}`, `Amount: $${ticketForPayment?.amountDue.toFixed(2)}, Card: ${cardBrand} ****${last4}`, ActivityLogTargetType.PAYMENT, ticketId);
    closeSquarePaymentModal();
  }, [closeSquarePaymentModal, handleAddCommunicationLog, ticketForPayment, logActivity, currentUserNameForLog]);

  const handleImportSquareInventory = useCallback(async (parsedItems: Partial<InventoryItem>[]) => {
    const allowedRoles: EmployeeRole[] = ['Administrator', 'Lead Technician', 'Senior Technician'];
    if (!currentUser || !allowedRoles.includes(currentUser.role as EmployeeRole) || currentUser.status === 'Inactive') {
        alert("You do not have permission to import inventory.");
        return;
    }
    setInventoryImportLoading(true);
    console.log("Importing Square inventory from CSV...", parsedItems);

    await new Promise(resolve => setTimeout(resolve, 1500));
    let newCount = 0;
    let updatedCount = 0;
    const now = new Date().toISOString();

    setInventoryItems(prevItems => {
      const updatedItemsMap = new Map(prevItems.map(item => [item.id, item]));

      parsedItems.forEach(csvItem => {
        let existingItem = prevItems.find(
          item => item.source === 'square' && item.sku && csvItem.sku && item.sku === csvItem.sku
        );
        if (!existingItem) {
            existingItem = prevItems.find(
                item => item.source === 'square' && item.name && csvItem.name && item.name.toLowerCase() === csvItem.name.toLowerCase()
            );
        }

        if (existingItem) {
          updatedItemsMap.set(existingItem.id, {
            ...existingItem,
            name: csvItem.name || existingItem.name,
            category: csvItem.category || existingItem.category,
            stock: typeof csvItem.stock === 'number' ? csvItem.stock : existingItem.stock,
            price: typeof csvItem.price === 'number' ? csvItem.price : existingItem.price,
            cost: typeof csvItem.cost === 'number' ? csvItem.cost : existingItem.cost,
            lastSyncedAt: now,
          });
          updatedCount++;
        } else {
          if (csvItem.name) {
            const newItemId = `INV-SQ-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
            updatedItemsMap.set(newItemId, {
              id: newItemId,
              source: 'square',
              name: csvItem.name,
              sku: csvItem.sku || undefined,
              category: csvItem.category || 'Uncategorized',
              stock: typeof csvItem.stock === 'number' ? csvItem.stock : 0,
              price: typeof csvItem.price === 'number' ? csvItem.price : 0,
              cost: typeof csvItem.cost === 'number' ? csvItem.cost : 0,
              lastSyncedAt: now,
              usageType: 'sale',
            });
            newCount++;
          }
        }
      });
      return Array.from(updatedItemsMap.values());
    });
    logActivity(currentUserNameForLog, "Imported Square Inventory from CSV", `${newCount} new, ${updatedCount} updated`, ActivityLogTargetType.INVENTORY);
    setLastInventoryImportTime(now);
    setInventoryImportLoading(false);
    alert(`${newCount} new Square items added and ${updatedCount} Square items updated from CSV.`);
    console.log("Square inventory CSV import complete.");
  }, [logActivity, currentUserNameForLog, currentUser]);

  const handleAddServiceItem = useCallback((itemData: Omit<InventoryItem, 'id' | 'lastSyncedAt' | 'source'>) => {
    const allowedRoles: EmployeeRole[] = ['Administrator', 'Lead Technician', 'Senior Technician'];
    if (!currentUser || !allowedRoles.includes(currentUser.role as EmployeeRole) || currentUser.status === 'Inactive') {
        alert("You do not have permission to add service items.");
        return;
    }
    const newItem: InventoryItem = {
      ...itemData,
      id: `INV-SVC-${Date.now()}-${Math.floor(Math.random()*1000)}`,
      source: 'service',
    };
    setInventoryItems(prev => [newItem, ...prev]);
    logActivity(currentUserNameForLog, `Added Service Item: ${newItem.name}`, `SKU: ${newItem.sku || 'N/A'}, Stock: ${newItem.stock}`, ActivityLogTargetType.INVENTORY, newItem.id);
  }, [logActivity, currentUserNameForLog, currentUser]);

  const handleUpdateServiceItem = useCallback((itemId: string, itemData: Omit<InventoryItem, 'id' | 'lastSyncedAt' | 'source'>) => {
    const allowedRoles: EmployeeRole[] = ['Administrator', 'Lead Technician', 'Senior Technician'];
     if (!currentUser || !allowedRoles.includes(currentUser.role as EmployeeRole) || currentUser.status === 'Inactive') {
        alert("You do not have permission to update service items.");
        return;
    }
    setInventoryItems(prev => prev.map(item =>
      item.id === itemId && item.source === 'service'
        ? { ...item, ...itemData }
        : item
    ));
    logActivity(currentUserNameForLog, `Updated Service Item: ${itemData.name}`, `ID: ${itemId}`, ActivityLogTargetType.INVENTORY, itemId);
  }, [logActivity, currentUserNameForLog, currentUser]);

  const handleDeleteServiceItem = useCallback((itemId: string) => {
    const allowedRoles: EmployeeRole[] = ['Administrator', 'Lead Technician', 'Senior Technician'];
    if (!currentUser || !allowedRoles.includes(currentUser.role as EmployeeRole) || currentUser.status === 'Inactive') {
        alert("You do not have permission to delete service items.");
        return;
    }
    const itemToDelete = inventoryItems.find(item => item.id === itemId);
    setInventoryItems(prev => prev.filter(item => item.id !== itemId || item.source !== 'service'));
    if (itemToDelete) {
      logActivity(currentUserNameForLog, `Deleted Service Item: ${itemToDelete.name}`, `ID: ${itemId}`, ActivityLogTargetType.INVENTORY, itemId);
    }
  }, [inventoryItems, logActivity, currentUserNameForLog, currentUser]);

  const openPrintModal = useCallback((title: string, content: React.ReactNode, contentClassName?: string) => {
    setPrintModalTitle(title);
    setPrintableContent(content);
    setPrintContentClassName(contentClassName);
    setIsPrintModalOpen(true);
  }, []);

  const closePrintModal = useCallback(() => {
    setIsPrintModalOpen(false);
    setPrintModalTitle('');
    setPrintableContent(null);
    setPrintContentClassName(undefined);
  }, []);

  const handleUpdateAppSettings = useCallback((newSettings: Partial<AppSettings>) => {
    if (currentUser?.role !== 'Administrator' || currentUser?.status === 'Inactive') {
        alert("You do not have permission to update application settings.");
        return;
    }
    const changedKeys = Object.keys(newSettings).filter(key => newSettings[key as keyof AppSettings] !== appSettings[key as keyof AppSettings]);
    setAppSettings(prevSettings => ({ ...prevSettings, ...newSettings }));
    logActivity(currentUserNameForLog, "Application Settings Updated", `Updated: ${changedKeys.join(', ')}`, ActivityLogTargetType.SETTINGS);
    alert('Application settings saved successfully!');
  }, [appSettings, logActivity, currentUserNameForLog, currentUser]);

  const handleUpdateBuybackSettings = useCallback((
    newModels: Record<BuybackDeviceType, DeviceModelInfo[]>,
    newFactors: BuybackPricingFactors
  ) => {
    if (currentUser?.role !== 'Administrator' || currentUser?.status === 'Inactive') {
      alert("You do not have permission to update buyback settings.");
      return;
    }
    setBuybackDeviceModels(newModels);
    setBuybackPricingFactors(newFactors);
    logActivity(currentUserNameForLog, "Buyback Program Settings Updated", "Device models and/or pricing factors were modified.", ActivityLogTargetType.SETTINGS);
    alert('Buyback program settings saved successfully!');
  }, [logActivity, currentUserNameForLog, currentUser]);


  const openSubmitSuggestionModal = useCallback(() => {
    if (currentUser?.status === 'Inactive') {
        alert("Inactive users cannot submit suggestions.");
        return;
    }
    setIsSubmitSuggestionModalOpen(true);
  }, [currentUser]);

  const closeSubmitSuggestionModal = useCallback(() => {
    setIsSubmitSuggestionModalOpen(false);
  }, []);

  const handleSubmitSuggestion = useCallback((suggestionText: string) => {
    console.log("Suggestion Submitted:", suggestionText);
    alert(`Thank you for your suggestion:\n"${suggestionText}"\n\n(This is a simulation. No data was sent.)`);
    logActivity(currentUserNameForLog, "Submitted Suggestion", suggestionText.substring(0,50)+'...', ActivityLogTargetType.SYSTEM);
    closeSubmitSuggestionModal();
  }, [closeSubmitSuggestionModal, logActivity, currentUserNameForLog]);

  const handleApproveEstimate = useCallback((ticketId: string) => {
    let customerName = "Customer";
    setTickets(prevTickets =>
      prevTickets.map(ticket => {
        if (ticket.id === ticketId) {
          customerName = ticket.customer;
          const updatedRepairProgress = ticket.repairProgress.map(stage => {
            if (stage.id === 'approval_wait') return { ...stage, status: RepairStageStatus.COMPLETED, completedAt: new Date().toISOString() };
            if (stage.id === 'repairing' && stage.status === RepairStageStatus.PENDING) return { ...stage, status: RepairStageStatus.IN_PROGRESS, startedAt: new Date().toISOString()};
             if (stage.id === 'estimate_sent' && stage.status === RepairStageStatus.PENDING) return { ...stage, status: RepairStageStatus.COMPLETED, completedAt: new Date().toISOString()};
            return stage;
          });
          handleAddCommunicationLog(
            ticketId,
            CommunicationType.CUSTOMER_PORTAL,
            "Estimate approved by customer via portal.",
            ticket.customerEmail || ticket.customerPhoneNumber,
            "Customer"
          );
          return { ...ticket, status: TicketStatus.IN_PROGRESS, isEstimateApproved: true, repairProgress: updatedRepairProgress, updatedAt: new Date().toISOString() };
        }
        return ticket;
      })
    );
    logActivity(customerName, `Approved Estimate for Ticket ${ticketId}`, `Via Customer Portal`, ActivityLogTargetType.TICKET, ticketId);
    alert(`Estimate for ticket ${ticketId} approved. Status changed to In Progress.`);
  }, [handleAddCommunicationLog, logActivity]);

  const handleDeclineEstimate = useCallback((ticketId: string) => {
    let customerName = "Customer";
    setTickets(prevTickets =>
      prevTickets.map(ticket => {
        if (ticket.id === ticketId) {
          customerName = ticket.customer;
          const updatedRepairProgress = ticket.repairProgress.map(stage => {
            if (stage.id === 'approval_wait') return { ...stage, status: RepairStageStatus.COMPLETED, completedAt: new Date().toISOString(), notes:'Customer Declined' };
            if (stage.id === 'estimate_sent' && stage.status === RepairStageStatus.PENDING) return { ...stage, status: RepairStageStatus.COMPLETED, completedAt: new Date().toISOString()};
            if (['parts_order', 'repairing', 'quality_check', 'pickup_ready', 'completed_paid'].includes(stage.id)) {
                return {...stage, status: RepairStageStatus.SKIPPED, completedAt: new Date().toISOString()};
            }
            return stage;
          });
          handleAddCommunicationLog(
            ticketId,
            CommunicationType.CUSTOMER_PORTAL,
            "Estimate declined by customer via portal.",
            ticket.customerEmail || ticket.customerPhoneNumber,
            "Customer"
          );
          return { ...ticket, status: TicketStatus.CUSTOMER_DECLINED, isEstimateApproved: false, repairProgress: updatedRepairProgress, updatedAt: new Date().toISOString() };
        }
        return ticket;
      })
    );
    logActivity(customerName, `Declined Estimate for Ticket ${ticketId}`, `Via Customer Portal`, ActivityLogTargetType.TICKET, ticketId);
    alert(`Estimate for ticket ${ticketId} declined. Status changed to Customer Declined.`);
  }, [handleAddCommunicationLog, logActivity]);

  const handleSaveBuybackRequest = useCallback((requestData: Omit<BuybackRequest, 'id' | 'createdAt' | 'status'>): string => {
    const newRequestId = `BB-${String(buybackRequests.length + 1 + Math.floor(Math.random() * 1000)).padStart(3, '0')}`;
    const newRequest: BuybackRequest = {
      ...requestData,
      id: newRequestId,
      createdAt: new Date().toISOString(),
      status: 'Pending Review',
    };
    setBuybackRequests(prev => [newRequest, ...prev]);
    logActivity(requestData.customerName, `Submitted Buyback Request ${newRequestId}`, `Device: ${newRequest.deviceModel}, Est. Value: $${newRequest.estimatedValue.toFixed(2)}`, ActivityLogTargetType.BUYBACK, newRequestId);
    console.log("New Buyback Request Submitted:", newRequest);
    return newRequestId;
  }, [buybackRequests, logActivity]);

  const handleCompleteBuybackAndAddToInventory = useCallback((
    buybackRequestId: string,
    finalValue: number,
    adminNotes?: string
  ) => {
    const request = buybackRequests.find(r => r.id === buybackRequestId);
    if (!request) {
      console.error("Buyback request not found for completion:", buybackRequestId);
      alert("Error: Buyback request not found.");
      return;
    }

    const inventoryDeviceId = `TID-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`;

    const newTradedInDevice: TradedInDevice = {
      id: inventoryDeviceId,
      buybackRequestId: request.id,
      originalCustomerName: request.customerName,
      originalCustomerEmail: request.customerEmail,
      deviceType: request.deviceType,
      deviceModelName: request.deviceModel,
      imeiOrSerial: request.imeiOrSerial,
      storageCapacity: request.storageCapacity,
      overallConditionAtTradeIn: request.overallCondition,
      finalTradeInValue: finalValue,
      dateAddedToInventory: new Date().toISOString(),
      currentStatus: 'In Stock',
      notes: adminNotes,
    };

    setTradedInDevices(prev => [newTradedInDevice, ...prev]);

    setBuybackRequests(prevRequests =>
      prevRequests.map(r =>
        r.id === buybackRequestId
          ? { ...r, status: 'Completed', finalOfferedValue: finalValue, currentInventoryDeviceId: inventoryDeviceId }
          : r
      )
    );

    logActivity(
      currentUserNameForLog,
      `Completed Buyback & Added to Inventory: ${inventoryDeviceId}`,
      `Device: ${newTradedInDevice.deviceModelName}, Value: $${finalValue.toFixed(2)}, From Request: ${buybackRequestId}`,
      ActivityLogTargetType.INVENTORY,
      inventoryDeviceId
    );
    alert(`Device ${newTradedInDevice.deviceModelName} added to traded-in inventory with ID ${inventoryDeviceId}.`);
  }, [buybackRequests, logActivity, currentUserNameForLog]);


  const ticketsWithSla = useMemo(() => {
    return tickets.map(ticket => {
      const slaInfo = calculateSlaForTicket(ticket);
      return { ...ticket, ...slaInfo };
    });
  }, [tickets]);

  const handleAddRepairChecklist = useCallback((checklist: Omit<RepairChecklist, 'id' | 'createdAt' | 'updatedAt'>) => {
    const newChecklist: RepairChecklist = {
      ...checklist,
      id: `CHKLST-${Date.now()}`,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    setRepairChecklists(prev => [newChecklist, ...prev]);
    logActivity(currentUserNameForLog, `Created Checklist: ${newChecklist.title}`, undefined, ActivityLogTargetType.SYSTEM, newChecklist.id);
  }, [logActivity, currentUserNameForLog]);

  const handleUpdateRepairChecklist = useCallback((checklistId: string, updatedData: Partial<Omit<RepairChecklist, 'id' | 'createdAt' | 'updatedAt'>>) => {
    setRepairChecklists(prev => prev.map(cl =>
      cl.id === checklistId ? { ...cl, ...updatedData, updatedAt: new Date().toISOString() } : cl
    ));
    logActivity(currentUserNameForLog, `Updated Checklist: ${updatedData.title || checklistId}`, undefined, ActivityLogTargetType.SYSTEM, checklistId);
  }, [logActivity, currentUserNameForLog]);

  const handleDeleteRepairChecklist = useCallback((checklistId: string) => {
    const checklistToDelete = repairChecklists.find(cl => cl.id === checklistId);
    setRepairChecklists(prev => prev.filter(cl => cl.id !== checklistId));
    if (checklistToDelete) {
      logActivity(currentUserNameForLog, `Deleted Checklist: ${checklistToDelete.title}`, undefined, ActivityLogTargetType.SYSTEM, checklistId);
    }
  }, [repairChecklists, logActivity, currentUserNameForLog]);

  const handleUpdateTicketChecklistProgress = useCallback((ticketId: string, checklistItemId: string, completed: boolean) => {
     if (currentUser?.status === 'Inactive') {
        alert("Inactive users cannot update checklist progress.");
        return;
    }
    setTickets(prevTickets => prevTickets.map(ticket => {
      if (ticket.id === ticketId) {
        const updatedChecklistProgress = {
          ...(ticket.checklistProgress || {}),
          [checklistItemId]: completed,
        };
        logActivity(currentUserNameForLog, `Updated Checklist Item for ${ticketId}`, `Item ${checklistItemId} to ${completed ? 'Complete' : 'Incomplete'}`, ActivityLogTargetType.TICKET, ticketId);
        return { ...ticket, checklistProgress: updatedChecklistProgress, updatedAt: new Date().toISOString() };
      }
      return ticket;
    }));
  }, [logActivity, currentUserNameForLog, currentUser]);


  const closeAppointmentModal = useCallback(() => {
    setIsAppointmentModalOpen(false);
    setAppointmentModalData(null);
  }, []);

  const handleAddAppointment = useCallback((appointmentData: Omit<Appointment, 'id' | 'createdAt' | 'updatedAt'>) => {
    const newAppointment: Appointment = {
      ...appointmentData,
      id: `APT-${Date.now()}`,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    setAppointments(prev => [newAppointment, ...prev].sort((a,b) => new Date(a.date + 'T' + a.startTime).getTime() - new Date(b.date + 'T' + b.startTime).getTime()));
    logActivity(currentUserNameForLog, `Created Appointment: ${newAppointment.id}`, `For: ${newAppointment.customerName} on ${newAppointment.date}`, ActivityLogTargetType.APPOINTMENT, newAppointment.id);
  }, [logActivity, currentUserNameForLog]);

  const handleUpdateAppointment = useCallback((appointmentId: string, updatedData: Partial<Omit<Appointment, 'id' | 'createdAt' | 'updatedAt'>>) => {
    setAppointments(prev => prev.map(apt =>
      apt.id === appointmentId ? { ...apt, ...updatedData, updatedAt: new Date().toISOString() } : apt
    ).sort((a,b) => new Date(a.date + 'T' + a.startTime).getTime() - new Date(b.date + 'T' + b.startTime).getTime()));
    logActivity(currentUserNameForLog, `Updated Appointment: ${appointmentId}`, `For: ${updatedData.customerName || 'customer'} on ${updatedData.date || 'date'}`, ActivityLogTargetType.APPOINTMENT, appointmentId);
  }, [logActivity, currentUserNameForLog]);

  const handleDeleteAppointment = useCallback((appointmentId: string) => {
    const apptToDelete = appointments.find(a => a.id === appointmentId);
    setAppointments(prev => prev.filter(apt => apt.id !== appointmentId));
    if (apptToDelete) {
       logActivity(currentUserNameForLog, `Deleted Appointment: ${appointmentId}`, `For: ${apptToDelete.customerName} on ${apptToDelete.date}`, ActivityLogTargetType.APPOINTMENT, appointmentId);
    }
  }, [appointments, logActivity, currentUserNameForLog]);

  const handleSaveAppointmentFromModal = (
      data: Appointment | Omit<Appointment, "id" | "createdAt" | "updatedAt">,
      _idFromModal?: string
  ) => {
      if ('id' in data && data.id) {
          const { id: appointmentId, createdAt, updatedAt, ...updatePayload } = data as Appointment;
          handleUpdateAppointment(appointmentId, updatePayload);
      } else {
          handleAddAppointment(data as Omit<Appointment, "id" | "createdAt" | "updatedAt">);
      }
  };

  const handleAddPartToTicket = useCallback((
    ticketId: string,
    inventoryItemId: string,
    quantityUsed: number,
    addPriceToTicketTotal: boolean
  ) => {
    if (currentUser?.status === 'Inactive') {
      alert("Inactive users cannot add parts to tickets.");
      return;
    }

    const inventoryItem = inventoryItems.find(item => item.id === inventoryItemId);
    if (!inventoryItem) {
      alert("Selected inventory item not found.");
      return;
    }

    if (inventoryItem.stock < quantityUsed) {
      alert(`Not enough stock for ${inventoryItem.name}. Available: ${inventoryItem.stock}, Requested: ${quantityUsed}.`);
      return;
    }

    setTickets(prevTickets => prevTickets.map(ticket => {
      if (ticket.id === ticketId) {
        const newPartUsageEntry: PartUsageEntry = {
          id: `PU-${ticketId}-${Date.now()}`,
          inventoryItemId,
          itemName: inventoryItem.name,
          quantityUsed,
          unitCostAtTimeOfUse: inventoryItem.cost,
          unitPriceAtTimeOfUse: inventoryItem.price,
          addedAt: new Date().toISOString(),
          addedBy: currentUserNameForLog,
        };

        const updatedTicket = {
          ...ticket,
          partsUsed: [...ticket.partsUsed, newPartUsageEntry],
          amountDue: addPriceToTicketTotal && typeof inventoryItem.price === 'number'
            ? ticket.amountDue + (inventoryItem.price * quantityUsed)
            : ticket.amountDue,
          updatedAt: new Date().toISOString(),
        };
        logActivity(currentUserNameForLog, `Added Part to ${ticketId}`, `${quantityUsed} x ${inventoryItem.name}. Price added to total: ${addPriceToTicketTotal}.`, ActivityLogTargetType.TICKET, ticketId);
        return updatedTicket;
      }
      return ticket;
    }));

    setInventoryItems(prevInventory => prevInventory.map(item =>
      item.id === inventoryItemId
        ? { ...item, stock: item.stock - quantityUsed }
        : item
    ));
  }, [inventoryItems, currentUserNameForLog, logActivity, currentUser]);


  const processedTickets = useMemo(() => {
    let tempTickets = [...ticketsWithSla];

    if (specialTicketFilterCriteria === 'unpaid') {
        tempTickets = tempTickets.filter(ticket =>
            (ticket.status === TicketStatus.READY_FOR_PICKUP || ticket.status === TicketStatus.COMPLETED) &&
            ticket.paymentStatus === PaymentStatus.UNPAID &&
            ticket.amountDue > 0
        );
    } else {
        if (ticketSearchTerm.trim()) {
        const lowerSearchTerm = ticketSearchTerm.toLowerCase();
        tempTickets = tempTickets.filter(ticket =>
            ticket.id.toLowerCase().includes(lowerSearchTerm) ||
            ticket.subject.toLowerCase().includes(lowerSearchTerm) ||
            ticket.customer.toLowerCase().includes(lowerSearchTerm) ||
            (ticket.deviceModel && ticket.deviceModel.toLowerCase().includes(lowerSearchTerm)) ||
            (ticket.customerEmail && ticket.customerEmail.toLowerCase().includes(lowerSearchTerm)) ||
            (ticket.customerPhoneNumber && ticket.customerPhoneNumber.includes(lowerSearchTerm)) ||
            (ticket.deviceSerialNumber && ticket.deviceSerialNumber.toLowerCase().includes(lowerSearchTerm))
        );
        }
        if (activeFilter !== 'ALL') {
        tempTickets = tempTickets.filter(ticket => ticket.status === activeFilter);
        }
    }

    tempTickets.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    return tempTickets;
  }, [ticketsWithSla, ticketSearchTerm, activeFilter, specialTicketFilterCriteria]);

  const handleSetTicketSearchTerm = useCallback((term: string) => {
    setSpecialTicketFilterCriteria(null);
    setTicketSearchTerm(term);
  }, []);

  const handleSetActiveFilter = useCallback((filter: FilterType) => {
    setSpecialTicketFilterCriteria(null);
    setActiveFilter(filter);
  }, []);

  const selectedCustomerDetails = useMemo(() => {
    return customers.find(c => c.id === selectedCustomerId);
  }, [customers, selectedCustomerId]);

  const ticketsForSelectedCustomer = useMemo(() => {
    if (!selectedCustomerDetails) return [];
    return ticketsWithSla.filter(ticket => ticket.customer === selectedCustomerDetails.name)
                  .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }, [ticketsWithSla, selectedCustomerDetails]);

  const newTicketsCount = useMemo(() => {
    return tickets.filter(ticket => ticket.status === TicketStatus.NEW).length;
  }, [tickets]);

  const PermissionDeniedPage: React.FC = () => (
    <div className="flex flex-col items-center justify-center h-full text-center p-10 bg-white rounded-lg shadow-xl">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-20 h-20 text-red-500 mb-4">
            <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
        </svg>
        <h2 className="text-3xl font-bold text-slate-700 mb-3">Access Denied</h2>
        <p className="text-slate-600 mb-6">You do not have permission to view this page or perform this action.</p>
        <Button onClick={showDashboardView} className="px-6 py-2 bg-[var(--color-primary)] text-white rounded-md hover:brightness-90 transition-colors">
            Go to Dashboard
        </Button>
    </div>
  );

  const renderMainContent = () => {
    const commonTicketHandlers = {
      onOpenUpdateStatusModal: openUpdateStatusModal,
      onViewCustomerProfile: handleViewCustomerByName,
      onOpenPaymentModal: openSquarePaymentModal,
      onOpenPrintModal: openPrintModal,
      onShowCustomerPortal: showCustomerPortalView,
      onAddInternalTicketNote: handleAddInternalTicketNote,
      onUpdateRepairStage: handleUpdateRepairStage,
      onAddCommunicationLog: handleAddCommunicationLog,
      onOpenAppointmentModal: openAppointmentModal,
      onAddPartToTicket: handleAddPartToTicket,
      inventoryItems: inventoryItems,
      appSettings: appSettings,
      currentUser: currentUser,
      repairChecklists: repairChecklists,
      onUpdateTicketChecklistProgress: handleUpdateTicketChecklistProgress,
    };

    switch (activeView) {
      case 'dashboard':
        return <Dashboard
                  allTickets={ticketsWithSla}
                  appointments={appointments}
                  onOpenTicketDetails={handleOpenTicketFromDashboard}
                  {...commonTicketHandlers}
                  appSettings={appSettings}
                  onNavigateToFullBuyback={showBuybackWidgetView}
                  ticketTrendData={ticketTrendData}
                  onShowUnpaidTickets={handleShowUnpaidTicketsView}
                  onNewTicket={openNewTicketModal}
                  onShowAppointments={showAppointmentsView}
                  repairBreakdownData={repairBreakdownData}
                  buybackDeviceModels={buybackDeviceModels}
                  buybackPricingFactors={buybackPricingFactors}
                />;
      case 'allTicketsView':
        return <AllTicketsPage
                  tickets={processedTickets}
                  allStatuses={STATUS_FILTERS}
                  activeStatusFilter={activeFilter}
                  onStatusFilterChange={handleSetActiveFilter}
                  searchTerm={ticketSearchTerm}
                  onSearchTermChange={handleSetTicketSearchTerm}
                  specialFilterActive={specialTicketFilterCriteria === 'unpaid'}
                  {...commonTicketHandlers}
                  onOpenAppointmentModal={openAppointmentModal}
               />;
      case 'ticketBoard':
        return <TicketBoardPage
                  tickets={ticketsWithSla}
                  allStatuses={Object.values(TicketStatus)}
                  onTicketDropUpdateStatus={handleUpdateTicketStatusFromBoard}
                  {...commonTicketHandlers}
                />;
      case 'customerSearch':
        return <CustomerSearchPage customers={customers} onSelectCustomer={showCustomerProfile} currentUser={currentUser} />;
      case 'customerProfile':
        return <CustomerProfilePage
                  customer={selectedCustomerDetails}
                  customerTickets={ticketsForSelectedCustomer}
                  employees={employees}
                  onAddCustomerNote={handleAddCustomerNote}
                  {...commonTicketHandlers}
                />;
      case 'inventory':
        return <InventoryPage
                  inventoryItems={inventoryItems}
                  onImportSquareInventory={handleImportSquareInventory}
                  importLoading={inventoryImportLoading}
                  lastInventoryImportTime={lastInventoryImportTime}
                  onOpenPrintModal={openPrintModal}
                  onAddServiceItem={handleAddServiceItem}
                  onUpdateServiceItem={handleUpdateServiceItem}
                  onDeleteServiceItem={handleDeleteServiceItem}
                  currentUser={currentUser}
                  appSettings={appSettings}
                  buybackRequests={buybackRequests}
                  tradedInDevices={tradedInDevices}
                />;
      case 'selfCheckIn':
        return <SelfCheckInPage onSaveTicket={handleSaveTicket} onGoHome={showDashboardView} appSettings={appSettings} />;
      case 'buybackWidget':
        return <BuybackWidgetPage
                  appSettings={appSettings}
                  onSaveRequest={handleSaveBuybackRequest}
                  onGoHome={showDashboardView}
                  initialDeviceType={quickAppraisalInitialData?.deviceType}
                  initialModelId={quickAppraisalInitialData?.modelId}
                  initialCondition={quickAppraisalInitialData?.condition}
                  buybackDeviceModels={buybackDeviceModels}
                  buybackPricingFactors={buybackPricingFactors}
                  onCompleteBuybackAndAddToInventory={handleCompleteBuybackAndAddToInventory}
                  currentUser={currentUser}
                />;
      case 'labels':
        return <LabelsPage
                  tickets={tickets}
                  inventoryItems={inventoryItems}
                  onOpenPrintModal={openPrintModal}
                  currentUser={currentUser}
                />;
      case 'appointments':
        return <AppointmentsPage
                  appointments={appointments}
                  employees={employees}
                  onOpenModal={openAppointmentModal}
                />;
      case 'adminUsers':
        return currentUser?.role === 'Administrator' ? <AdminUsersPage employees={employees} currentUser={currentUser} /> : <PermissionDeniedPage />;
      case 'adminSettings':
        return currentUser?.role === 'Administrator' ? <AdminSettingsPage
                  settings={appSettings}
                  onSaveSettings={handleUpdateAppSettings}
                  currentUser={currentUser}
                /> : <PermissionDeniedPage />;
      case 'adminUICustomization':
        return currentUser?.role === 'Administrator' ? <AdminUICustomizationPage
                  settings={appSettings}
                  onSaveSettings={handleUpdateAppSettings}
                  currentUser={currentUser}
                /> : <PermissionDeniedPage />;
      case 'adminActivityLog':
        return currentUser?.role === 'Administrator' ? <AdminActivityLogPage logEntries={activityLog} /> : <PermissionDeniedPage />;
      case 'adminBuybackSettings':
        return currentUser?.role === 'Administrator' ? <AdminBuybackSettingsPage
                  buybackDeviceModels={buybackDeviceModels}
                  buybackPricingFactors={buybackPricingFactors}
                  onSaveBuybackSettings={handleUpdateBuybackSettings}
                  currentUser={currentUser}
                /> : <PermissionDeniedPage />;
       case 'reports':
        return currentUser?.role === 'Administrator' || currentUser?.role === 'Manager' || currentUser?.role === 'Lead Technician'
                ? <ReportsPage ticketsWithSla={ticketsWithSla} employees={employees} currentUser={currentUser} inventoryItems={inventoryItems} />
                : <PermissionDeniedPage />;
      case 'knowledgeBase':
        return <KnowledgeBasePage settings={appSettings} />;
      case 'aiTroubleshooter':
        return <AITroubleshooterPage />;
      case 'aiDiagnosticAssistant':
        return <AIDiagnosticAssistantPage />;
      case 'customerPortal':
        const portalTicket = ticketsWithSla.find(t => t.id === ticketIdForCustomerPortal);
        return <CustomerPortalPage
                  ticket={portalTicket}
                  appSettings={appSettings}
                  onMakePayment={openSquarePaymentModal}
                  onApproveEstimate={() => portalTicket && handleApproveEstimate(portalTicket.id)}
                  onDeclineEstimate={() => portalTicket && handleDeclineEstimate(portalTicket.id)}
                  onNavigateBack={showDashboardView}
               />;
      case 'permissionDenied':
        return <PermissionDeniedPage />;
      default:
        return <Dashboard
                  allTickets={ticketsWithSla}
                  appointments={appointments}
                  onOpenTicketDetails={handleOpenTicketFromDashboard}
                  {...commonTicketHandlers}
                  appSettings={appSettings}
                  onNavigateToFullBuyback={showBuybackWidgetView}
                  ticketTrendData={ticketTrendData}
                  onShowUnpaidTickets={handleShowUnpaidTicketsView}
                  onNewTicket={openNewTicketModal}
                  onShowAppointments={showAppointmentsView}
                  repairBreakdownData={repairBreakdownData}
                  buybackDeviceModels={buybackDeviceModels}
                  buybackPricingFactors={buybackPricingFactors}
               />;
    }
  };

  const isPortalView = activeView === 'selfCheckIn' || activeView === 'customerPortal' || activeView === 'buybackWidget';

  return (
    <div className="flex flex-col min-h-screen bg-slate-100">
      {!isPortalView &&
        <Header
            appSettings={appSettings}
            currentUser={currentUser}
            allUsers={MOCK_EMPLOYEES}
            onSetCurrentUser={handleSetCurrentUser}
        />
      }
      <div className={`flex flex-1 ${isPortalView ? 'h-screen' : 'max-h-[calc(100vh-var(--header-height,64px)-var(--footer-height,56px))] overflow-hidden'}`}>
        {!isPortalView && (
          <Sidebar
            activeView={activeView}
            newTicketsCount={newTicketsCount}
            onNewTicket={openNewTicketModal}
            onShowDashboard={showDashboardView}
            onShowAllTickets={() => {
                setSpecialTicketFilterCriteria(null);
                showAllTicketsView(undefined, 'ALL');
            }}
            onShowTicketBoard={showTicketBoardView}
            onShowAppointments={showAppointmentsView}
            onShowReports={showReportsView}
            onShowCustomerSearch={showCustomerSearchView}
            onShowInventory={showInventoryView}
            onShowSelfCheckIn={showSelfCheckInView}
            onShowBuybackWidget={showBuybackWidgetView}
            onShowLabels={showLabelsView}
            onShowAdminUsers={showAdminUsersView}
            onShowAdminSettings={showAdminSettingsView}
            onShowAdminBuybackSettings={showAdminBuybackSettingsView}
            onShowAdminUICustomization={showAdminUICustomizationView}
            onShowAdminActivityLog={showAdminActivityLogView}
            onOpenSubmitSuggestion={openSubmitSuggestionModal}
            onShowKnowledgeBase={showKnowledgeBaseView}
            onShowAITroubleshooter={showAITroubleshooterView}
            onShowAIDiagnosticAssistant={showAIDiagnosticAssistantView}
            currentUser={currentUser}
          />
        )}
        <main className={`flex-1 overflow-y-auto ${isPortalView ? 'p-0 relative' : 'p-6'}`}>
          {renderMainContent()}
        </main>
      </div>
      {!isPortalView && <Footer />}
      {isNewTicketModalOpen && <NewTicketModal
        isOpen={isNewTicketModalOpen}
        onClose={closeNewTicketModal}
        onSave={handleSaveTicket}
        currentUser={currentUser}
      />}
      {ticketToUpdateStatus && (
        <UpdateStatusModal
          isOpen={isUpdateStatusModalOpen}
          onClose={closeUpdateStatusModal}
          onRequestStatusUpdate={handleRequestStatusUpdate}
          ticketId={ticketToUpdateStatus.id}
          currentStatus={ticketToUpdateStatus.currentStatus}
          currentUser={currentUser}
        />
      )}
      {isPreCompletionModalOpen && dataForPreCompletionCheck && (
        <PreCompletionCheckModal
            isOpen={isPreCompletionModalOpen}
            onClose={closePreCompletionModal}
            data={dataForPreCompletionCheck}
            onProceed={handleProceedWithStatusUpdateFromPreCheck}
            onSaveNotesAndClose={handleSavePreCheckNotesAndClose}
            appSettings={appSettings}
            repairChecklists={repairChecklists}
        />
      )}
      {isSquarePaymentModalOpen && ticketForPayment && (
        <SquarePaymentModal
          isOpen={isSquarePaymentModalOpen}
          onClose={closeSquarePaymentModal}
          ticketDetails={ticketForPayment}
          onPaymentSuccess={handlePaymentSuccess}
        />
      )}
      {isPrintModalOpen && (
        <PrintModal
          isOpen={isPrintModalOpen}
          onClose={closePrintModal}
          title={printModalTitle}
          contentClassName={printContentClassName}
        >
          {printableContent}
        </PrintModal>
      )}
      {isSubmitSuggestionModalOpen && (
        <SubmitSuggestionModal
          isOpen={isSubmitSuggestionModalOpen}
          onClose={closeSubmitSuggestionModal}
          onSubmit={handleSubmitSuggestion}
        />
      )}
      {isAppointmentModalOpen && (
        <AppointmentModal
          isOpen={isAppointmentModalOpen}
          onClose={closeAppointmentModal}
          onSaveAppointment={handleSaveAppointmentFromModal}
          initialData={appointmentModalData || {}}
          employees={MOCK_EMPLOYEES.filter(e => e.status === 'Active' && e.role.toLowerCase().includes('technician'))}
          existingAppointments={appointments}
        />
      )}
    </div>
  );
};

export default App;
